/** @jsx jsx */

import { useState } from 'react';

import {
  Button,
  Img,
  ModalDialogRoot,
  ModalDialogHeader,
  ModalDialogBody,
  ModalDialogFooter,
  Text,
} from '@zapier/design-system';
import { ClassNames, css, jsx } from '@emotion/core';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import { useAvroEvent } from 'app/developer-v3/hooks';

const Styles = {
  bodyWrapper: css`
    padding: 0px 26px 1em;
  `,
  list: css`
    display: inline-block;
    list-style-position: outside;
    margin-left: 1em;
    text-align: left;
  `,
  messages: css`
    margin-bottom: 0px;
    padding: 0px 52px;
  `,
};

type Props = {
  onCancel: () => void;
  onConfirm: () => Promise<void>;
};

function GoPublicModal({ onCancel, onConfirm }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { emitUserInteractionEvent } = useAvroEvent();

  return (
    <ClassNames>
      {({ css }) => (
        <ModalDialogRoot
          isOpen
          onOpenChange={open => {
            if (!open) onCancel();
          }}
          isDismissable={isSubmitting}
          dialogClassName={css({
            maxWidth: '850px',
          })}
        >
          <ModalDialogHeader>
            <Text tag="h1" type="SectionHeader">
              Take Integration Public
            </Text>
          </ModalDialogHeader>
          <ModalDialogBody>
            <div css={Styles.bodyWrapper}>
              <div css={Styles.messages}>
                <Text tag="p" type="Body1">
                  If you’ve changed your mind and would like to publish your app
                  on the Zapier App Directory, you can change the Intended
                  Audience for your integration.
                </Text>
                <Text margin="1em 0px" tag="h2" type="Body3">
                  What You Need to Know
                </Text>
                <ol css={Styles.list}>
                  <li>
                    Integrations intended for a public audience are still
                    private until they’ve gone through our Publishing review
                    process. Anyone you've invited to your integration will
                    still have access to it.
                  </li>
                  <li>
                    You can change the Intended Audience for your integration
                    back to private at any time before publishing by going to
                    the Integration settings.
                  </li>
                  <li>
                    After you make the change, you will see new options in the
                    sidebar to publish your integration and embed Zapier within
                    your application.
                  </li>
                </ol>
              </div>
              <Img
                alt="putlic intent"
                src="//cdn.zappy.app/49202d587faf231dab7a97278ae84cae.png"
              />
            </div>
          </ModalDialogBody>
          <ModalDialogFooter>
            <Button
              variant="secondary"
              isDisabled={isSubmitting}
              onPress={() => onCancel()}
              size="small"
            >
              Cancel
            </Button>
            <AsyncButton
              variant="primary"
              isDisabled={isSubmitting}
              isWorking={isSubmitting}
              onPress={async () => {
                try {
                  setIsSubmitting(true);
                  await onConfirm();
                  setIsSubmitting(false);
                  onCancel();
                  emitUserInteractionEvent({
                    eventAction: 'click',
                    interactionName:
                      'Confirm public intent from integration home',
                  });
                } catch {
                  setIsSubmitting(false);
                }
              }}
              size="small"
            >
              Confirm
            </AsyncButton>
          </ModalDialogFooter>
        </ModalDialogRoot>
      )}
    </ClassNames>
  );
}

export default GoPublicModal;
